import { useMemo } from 'react';
import { useCurrentUser } from 'src/hooks';
import { CartApprovalDetails, CartApprovalStatus } from 'src/types';
import { CartApprovalHistory } from './CartApprovalHistory';
import { CartApprovedPendingRejected } from './CartApprovedPendingRejected';

type Props = {
  cartApprovalDetails: CartApprovalDetails | null;
  cartApprovalStatus: CartApprovalStatus | null;
  cartId: string;
  includeLink: boolean;
  requestedByEmail: string;
  fromHistory?: boolean;
  whitePendingStatus?: boolean;
  page: 'get' | 'review' | 'history';
  handleContinue?: (skipApproval: boolean) => void;
};

export const CartApprovalDisplay = ({
  cartApprovalDetails, // this is the array of cart approval details
  cartApprovalStatus, // this is the most recent status of the cart approval
  cartId,
  includeLink,
  requestedByEmail,
  fromHistory = false,
  whitePendingStatus = false,
  page,
  handleContinue,
}: Props) => {
  const { user } = useCurrentUser();

  const pendingTextDisplay = useMemo(() => {
    // if the cart is not pending, we don't need to do anything
    if (cartApprovalStatus !== 'pending') {
      return null;
    }

    // At some point in time, the user requested the cart be approved by someone else.
    // it doesn't matter if they were the original requester or not.
    // we know the cart is still pending approval because cartApprovalStatus is the most recent layer.
    // if the user has not requested the cart to be approved and they can see the approval request pending, they can approve it
    // so we display 'Needs Your Approval' even if the user might not be explicitly requested to approve it.
    const currentUserRequestedCart = cartApprovalDetails?.some(
      (detail) => !!user?.key && detail.requestedBy.id === user.key,
    );

    if (currentUserRequestedCart) {
      return 'Sent for Approval';
    } else {
      return 'Needs Your Approval';
    }
  }, [cartApprovalDetails, user, cartApprovalStatus]);

  // link to get page if the user is looking at their own cart request
  const linkPage =
    requestedByEmail === user?.email ||
    (user?.subscription.role !== 'admin' &&
      user?.subscription.role !== 'approver')
      ? 'get'
      : 'review';

  if (!cartApprovalStatus) {
    return null;
  }

  return (
    <div className='flex min-h-[36px] flex-row flex-nowrap items-center justify-center gap-2'>
      <CartApprovalHistory cartApprovalDetails={cartApprovalDetails || []} />
      <CartApprovedPendingRejected
        cartApprovalDetails={cartApprovalDetails}
        cartApprovalStatus={cartApprovalStatus}
        requestedByEmail={requestedByEmail}
        includeLink={includeLink}
        linkPage={linkPage}
        cartId={cartId}
        fromHistory={fromHistory}
        whitePendingStatus={whitePendingStatus}
        pendingTextDisplay={pendingTextDisplay || ''}
        page={page}
        handleContinue={handleContinue}
      />
    </div>
  );
};
